import '@/App.css';
import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, ProtectedRoute } from '@/context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from '@/context/SiteSettingsContext';
import Navigation from '@/components/layout/Navigation';
import Footer from '@/components/layout/Footer';

import Home from '@/pages/Home';
import Services from '@/pages/Services';
import ServiceDetail from '@/pages/ServiceDetail';
import Industries from '@/pages/Industries';
import IndustryDetail from '@/pages/IndustryDetail';
import About from '@/pages/About';
import CaseExperience from '@/pages/CaseExperience';
import CaseDetail from '@/pages/CaseDetail';
import Insights from '@/pages/Insights';
import InsightDetail from '@/pages/InsightDetail';
import Contact from '@/pages/Contact';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Terms from '@/pages/Terms';
import Solutions from '@/pages/Solutions';
import IndiaSolutions from '@/pages/IndiaSolutions';
import GlobalSolutions from '@/pages/GlobalSolutions';

import AdminLogin from '@/admin/AdminLogin';
import AdminLayout from '@/admin/AdminLayout';
import AdminDashboard from '@/admin/AdminDashboard';
import AdminBlog from '@/admin/AdminBlog';
import AdminCases from '@/admin/AdminCases';
import AdminInquiries from '@/admin/AdminInquiries';
import AdminSettings from '@/admin/AdminSettings';
import AdminHomepage from '@/admin/AdminHomepage';
import AdminServices from '@/admin/AdminServices';
import AdminIndustries from '@/admin/AdminIndustries';

import NotFound from '@/pages/NotFound';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/** Injects GA / GTM scripts and updates favicon dynamically from site settings. */
function SiteEffects() {
  const s = useSiteSettings();

  useEffect(() => {
    const faviconUrl = s.favicon_url || (s.favicon_id ? `/api/media/${s.favicon_id}` : null);
    if (!faviconUrl) return;
    let link = document.querySelector('link[rel="icon"]') || document.querySelector('link[rel="shortcut icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, [s.favicon_id, s.favicon_url]);

  useEffect(() => {
    if (!s.ga_id || document.getElementById('ga-script')) return;
    const script = document.createElement('script');
    script.id = 'ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${s.ga_id}`;
    document.head.appendChild(script);
    const inline = document.createElement('script');
    inline.id = 'ga-inline';
    inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${s.ga_id}');`;
    document.head.appendChild(inline);
  }, [s.ga_id]);

  useEffect(() => {
    if (!s.gtm_id || document.getElementById('gtm-script')) return;
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${s.gtm_id}');`;
    document.head.appendChild(script);
  }, [s.gtm_id]);

  useEffect(() => {
    if (!s.meta_pixel_id || document.getElementById('fbq-script')) return;
    const script = document.createElement('script');
    script.id = 'fbq-script';
    script.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${s.meta_pixel_id}');fbq('track','PageView');`;
    document.head.appendChild(script);
  }, [s.meta_pixel_id]);

  return null;
}

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main id="main-content" className="flex-grow" tabIndex="-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SiteSettingsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SiteEffects />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/india" element={<IndiaSolutions />} />
              <Route path="/solutions/global" element={<GlobalSolutions />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/industries/:slug" element={<IndustryDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/case-experience" element={<CaseExperience />} />
              <Route path="/case-experience/:slug" element={<CaseDetail />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<InsightDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="cases" element={<AdminCases />} />
              <Route path="inquiries" element={<AdminInquiries />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="homepage" element={<AdminHomepage />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="industries" element={<AdminIndustries />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </SiteSettingsProvider>
    </AuthProvider>
  );
}

export default App;
